import { UntitledIcon } from '@faceup/icons'
import { ulHome01 } from '@faceup/icons/ulHome01'
import { ulRefreshCw01 } from '@faceup/icons/ulRefreshCw01'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { useLocation, useRouteError } from '@faceup/router'
import { useComponentDidMount } from '@faceup/ui'
import { Button, Result } from '@faceup/ui-base'
import { errorMessages } from '@faceup/utils'
import { NoShortIdError } from '../errors/NoShortIdError'
import { getShortIdFromPath } from '../utils/shortId'
import { UnavailableLink } from './UnavailableLink'

const messages = defineMessages('followUp.components.routerError', [
  { id: 'description', defaultMessage: 'Something went wrong.' },
  {
    id: 'hint',
    defaultMessage: 'Please try again. If the problem persists, go back to the home page.',
  },
  { id: 'buttonBackHome', defaultMessage: 'Back to home page' },
  { id: 'buttonReload', defaultMessage: 'Reload' },
] as const)

/**
 * The route wrappers catch most of these, but a page query below the Outlet can be
 * the first to notice the institution is gone, and it lands here instead.
 */
const isUnavailableLinkError = (error: unknown) =>
  error instanceof Error &&
  [errorMessages.reportSourceDoesNotExist, errorMessages.institutionDoesNotExist].some(message =>
    error.message.includes(message)
  )

export const RouterError = () => {
  const error = useRouteError()
  const location = useLocation()

  useComponentDidMount(() => {
    if (error instanceof NoShortIdError) {
      window.location.href = '/'
    }
  })

  if (isUnavailableLinkError(error)) {
    return <UnavailableLink shortId={getShortIdFromPath(location.pathname) ?? undefined} />
  }

  return (
    <div className='flex h-screen flex-col items-center justify-center'>
      <Result
        status='error'
        title={<FormattedMessage message={messages.description} />}
        subTitle={<FormattedMessage message={messages.hint} />}
        extra={
          <div className='inline-flex flex-col gap-2'>
            <Button
              type='default'
              onClick={() => window.location.reload()}
              icon={<UntitledIcon icon={ulRefreshCw01} />}
            >
              <FormattedMessage message={messages.buttonReload} />
            </Button>

            <Button
              type='primary'
              onClick={() => (window.location.href = '/')}
              icon={<UntitledIcon icon={ulHome01} />}
            >
              <FormattedMessage message={messages.buttonBackHome} />
            </Button>
          </div>
        }
      />
    </div>
  )
}
